// Packages
import React, { useRef, useState, useEffect, useCallback, useContext } from "react";
import { useSelector } from "react-redux";
import {
  useJsApiLoader,
  GoogleMap,
  PolygonF,
} from "@react-google-maps/api";
import { useIntl, FormattedMessage } from 'react-intl';
import { debounce } from "throttle-debounce";
// Icons, Images etc.
import GmapMarker from "../../images/gmap-marker.svg";
// Contexts
import { LocaleContext } from "../../wrappers/localeWrapper";
import { EnvContext } from "../envContext";
// Redux Operations
import { RootState } from "../../state/store";

declare global {
  interface Window {
    google: any;
  }
}

interface DeliveryAreaMapProps {
  lat?: number;
  lng?: number;
  onAreaChange: any;
  source?: string;
  reportAreaServiceabilityError: () => void;
  removeAreaServiceabilityError: () => void;
}

const mapOptions = {
  fullscreenControl: false,
  zoomControl: true,
  disableDoubleClickZoom: false,
  mapTypeControl: false,
  streetViewControl: false,
};

const LIBRARIES: ("places" | "geometry")[] = ["places", "geometry"];
const DEBOUNCE_TIME = 400;
const MINIMUM_CHARACTERS = 3;

// Inner component that renders when API is loaded
function DeliveryAreaMapContent(props: DeliveryAreaMapProps) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const autocompleteContainerRef = useRef<HTMLDivElement>(null);
  // Initialize markerPosition from props to ensure saved addresses show correct location
  const [markerPosition, setMarkerPosition] = useState<{ lat: number; lng: number }>({
    lat: props.lat || 0,
    lng: props.lng || 0
  });
  const [isLocationServiceable, setIsLocationServiceable] = useState(true);
  const [polygonOptions, setPolygonOptions] = useState({} as any);

  // Autocomplete state
  const [inputValue, setInputValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | null>(null);
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService | null>(null);

  const intl = useIntl();

  const { company } = useSelector((state: RootState) => state);
  const { selectedArea } = useSelector((state: RootState) => state.areas);

  // Debounced search query setter
  const debouncedSetSearchQuery = useCallback(
    debounce(DEBOUNCE_TIME, (value: string) => {
      setSearchQuery(value);
    }),
    []
  );

  const validateMarkedLocation = (lat: any, lng: any) => {
    if (props.source == "saved-address") {
      setIsLocationServiceable(true);
      return true;
    }

    let parsedPosition = new google.maps.LatLng(lat, lng);
    let zoneMapEnabled = company.configuration?.zone_mapping_enabled == true;
    let isServiceable = true;
    if (zoneMapEnabled && selectedArea?.mapped_zones?.length) {
      let zonePolygon = new google.maps.Polygon({
        paths: selectedArea.mapped_zones,
      });
      setTimeout(() => {
        isServiceable = google.maps.geometry.poly.containsLocation(
          parsedPosition,
          zonePolygon
        );
        setIsLocationServiceable(isServiceable);
      }, 200);
    } else {
      setIsLocationServiceable(true);
    }
    return isServiceable;
  };

  useEffect(() => {
    let lat = props.lat || 0.0;
    let lng = props.lng || 0.0;
    setMarkerPosition({ lat: lat, lng: lng });
  }, [props.lat, props.lng]);

  // Initialize autocomplete and places services
  useEffect(() => {
    if (window.google?.maps?.places) {
      const autocomplete = new window.google.maps.places.AutocompleteService();
      setAutocompleteService(autocomplete);

      const dummyDiv = document.createElement("div");
      const places = new window.google.maps.places.PlacesService(dummyDiv);
      setPlacesService(places);
    }
  }, []);

  // Trigger initial geocoding when marker position is set (equivalent to LoadScript onLoad)
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (!initialLoadDone.current && markerPosition.lat && markerPosition.lng && window.google?.maps) {
      initialLoadDone.current = true;
      if (validateMarkedLocation(markerPosition.lat, markerPosition.lng)) {
        let geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ latLng: markerPosition }, (responses: any) => {
          if (responses && responses[0]) {
            let streetName = responses[0].formatted_address;
            props.onAreaChange(streetName, markerPosition.lat, markerPosition.lng);
            setInputValue(streetName);
          }
        });
      }
    }
  }, [markerPosition.lat, markerPosition.lng]);

  // Handle clicks outside autocomplete dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (autocompleteContainerRef.current && !autocompleteContainerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, { passive: true });

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // Fetch predictions when searchQuery changes (after debounce)
  useEffect(() => {
    if (searchQuery.length >= MINIMUM_CHARACTERS && autocompleteService) {
      setIsLoading(true);
      autocompleteService.getPlacePredictions(
        { input: searchQuery },
        (predictions, status) => {
          setIsLoading(false);
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions);
            setShowDropdown(true);
          } else {
            setSuggestions([]);
            setShowDropdown(false);
          }
        }
      );
    } else {
      setSuggestions([]);
      setShowDropdown(false);
      setIsLoading(false);
    }
  }, [searchQuery, autocompleteService]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    if (value.length >= MINIMUM_CHARACTERS) {
      setIsLoading(true);
    }

    debouncedSetSearchQuery(value);
  };

  const handleSuggestionClick = (suggestion: google.maps.places.AutocompletePrediction) => {
    if (!placesService) return;

    setInputValue(suggestion.description);
    setShowDropdown(false);
    setSuggestions([]);

    placesService.getDetails(
      { placeId: suggestion.place_id, fields: ["geometry"] },
      (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          var svgElem = (document.getElementsByClassName("gmap-marker")[0] as HTMLObjectElement)?.getSVGDocument?.();
          svgElem?.children[0]?.children[1]?.firstElementChild?.classList.add("dragging");
          svgElem?.children[0]?.children[2]?.classList.add("dragging");

          if (validateMarkedLocation(lat, lng)) {
            props.onAreaChange(suggestion.description, lat, lng);
            setMarkerPosition({ lat, lng });
          }

          setTimeout(() => {
            svgElem?.children[0]?.children[1]?.firstElementChild?.classList.remove("dragging");
            svgElem?.children[0]?.children[2]?.classList.remove("dragging");
          }, 200);
        }
      }
    );
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowDropdown(true);
    }
  };

  useEffect(() => {
    if (props.source == "new-address") {
      if (!isLocationServiceable) {
        props.reportAreaServiceabilityError();
      } else {
        props.removeAreaServiceabilityError();
      }
    }
  }, [isLocationServiceable, markerPosition]);

  useEffect(() =>{
    setPolygonOptions({
      fillColor: "#24ad54",
      fillOpacity: 0.3,
      strokeColor: "#24ad54",
      strokeOpacity: 1,
      strokeWeight: 2,
      clickable: false,
      draggable: false,
      editable: false,
      geodesic: false,
      paths: selectedArea?.mapped_zones || [],
      zIndex: 1,
    });
  }, [selectedArea]);

  const onMapLoad = useCallback((map: any) => {
    mapRef.current = map;
  }, []);

  const onMapClick = useCallback((e: any) => {
    var svgElem = (document.getElementsByClassName("gmap-marker")[0] as HTMLObjectElement)?.getSVGDocument?.();
    svgElem?.children[0]?.children[1]?.firstElementChild?.classList.add("dragging");
    svgElem?.children[0]?.children[2]?.classList.add("dragging");

    if (validateMarkedLocation(e.latLng.lat(), e.latLng.lng())) {
      updateArea(e.latLng.lat(), e.latLng.lng());
    }

    setTimeout(() => {
      svgElem?.children[0]?.children[1]?.firstElementChild?.classList.remove("dragging");
      svgElem?.children[0]?.children[2]?.classList.remove("dragging");
    }, 200);
  }, []);

  const onMapDragStart = useCallback(() => {
    var svgElem = (document.getElementsByClassName("gmap-marker")[0] as HTMLObjectElement)?.getSVGDocument?.();
    svgElem?.children[0]?.children[1]?.firstElementChild?.classList.add("dragging");
    svgElem?.children[0]?.children[2]?.classList.add("dragging");
  }, []);

  const onMapDragEnd = useCallback(() => {
    const center = mapRef.current?.getCenter?.();
    if (mapRef.current && center && validateMarkedLocation(center.lat(), center.lng())) {
      updateArea(center.lat(), center.lng());
    }
    var svgElem = (document.getElementsByClassName("gmap-marker")[0] as HTMLObjectElement)?.getSVGDocument?.();
    svgElem?.children[0]?.children[1]?.firstElementChild?.classList.remove("dragging");
    svgElem?.children[0]?.children[2]?.classList.remove("dragging");
  }, []);

  const onMapZoomChanged = useCallback(() => {
    const center = mapRef.current?.getCenter?.();
    if (mapRef.current && center && validateMarkedLocation(center.lat(), center.lng())) {
      updateArea(center.lat(), center.lng());
    }
    setTimeout(()=>{
      var svgElem = (document.getElementsByClassName("gmap-marker")[0] as HTMLObjectElement)?.getSVGDocument?.();
      svgElem?.children[0]?.children[1]?.firstElementChild?.classList.remove("dragging");
      svgElem?.children[0]?.children[2]?.classList.remove("dragging");
    },200);
  }, []);

  const updateArea = (lat: any, lng: any) => {
    let geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ latLng: { lat: lat, lng: lng } }, (responses: any) => {
      if (responses && responses[0]) {
        let streetName = responses[0].formatted_address;
        props.onAreaChange(responses[0].formatted_address, lat, lng);
        setInputValue(streetName);
      }
    });
    setMarkerPosition({ lat: lat, lng: lng });
  };

  const placeholder = intl.formatMessage({
    id: "googlemap.search_location_here",
    defaultMessage: "Search your location here..."
  });

  return (
    <React.Fragment>
      <div className="gmap-marker-wrapper">
        <object
          className="gmap-marker"
          type="image/svg+xml"
          data={GmapMarker}
        ></object>
      </div>
      <GoogleMap
        mapContainerStyle={{
          height: "600px",
        }}
        center={markerPosition}
        zoom={16}
        options={mapOptions}
        onLoad={onMapLoad}
        onClick={onMapClick}
        onDragStart={onMapDragStart}
        onDragEnd={onMapDragEnd}
        onZoomChanged={onMapZoomChanged}
      >
        <PolygonF
          path={selectedArea?.mapped_zones || []}
          options={polygonOptions}
        />

        {/* Custom Autocomplete with debounce */}
        <div
          className="delivery-area-map-autocomplete-container"
          ref={autocompleteContainerRef}
        >
          <input
            className="delivery-area-map-input"
            type="text"
            id="map-input"
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            autoComplete="off"
            style={{ paddingRight: isLoading ? "32px" : "12px" }}
          />
          {isLoading && (
            <span className="autocomplete-loading-indicator">
              <span className="spinner"></span>
            </span>
          )}
          {showDropdown && suggestions.length > 0 && (
            <ul className="autocomplete-suggestions-dropdown">
              {suggestions.map((suggestion) => (
                <li
                  key={suggestion.place_id}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="autocomplete-suggestion-item"
                >
                  {suggestion.description}
                </li>
              ))}
            </ul>
          )}
        </div>
      </GoogleMap>
    </React.Fragment>
  );
}

// Wrapper component that handles loading the Google Maps API
function DeliveryAreaMap(props: DeliveryAreaMapProps) {
  const { googleMapKey } = useContext(EnvContext);
  const localeContext = useContext(LocaleContext) as { locale: string; updateLocale: (locale: string) => void };

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: googleMapKey || "",
    libraries: LIBRARIES,
    language: localeContext.locale,
  });

  if (!googleMapKey) {
    return null;
  }

  if (loadError) {
    return <div><FormattedMessage id="maps.error_loading" /></div>;
  }

  if (!isLoaded) {
    return <div style={{ height: "600px", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <FormattedMessage id="maps.loading" />
    </div>;
  }

  return <DeliveryAreaMapContent {...props} />;
}

export default React.memo(DeliveryAreaMap);
